import React, { Fragment, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { translate } from '../../Services/translation';
import { cn } from '../../Helpers/cn';
import OrderStatus from '../primitives/OrderStatus';
import { parseAsBoolean, useQueryState } from 'nuqs';

const OrderHistoryB2CDetail = ({ onDismiss, isReseller, printPageBaseUrl }) => {
  const { order } = useSelector((state) => state.myPage.orders);
  const { tabsConstants } = useSelector((state) => state.myPage);

  const [isPrintMode] = useQueryState(
    'print',
    parseAsBoolean.withDefault(false)
  );

  useEffect(() => {
    if (isPrintMode && order) {
      const timer = setTimeout(() => {
        window.print();
      }, 1000); // Delay to ensure content is fully rendered before printing

      return () => clearTimeout(timer);
    }
  }, [isPrintMode, order]);

  return (
    <Fragment>
      <div className="mb-4 flex flex-col justify-between md:flex-row">
        <button
          className="order-link-inverted inline-flex items-center gap-1 font-extrabold"
          onClick={onDismiss}
          type="button"
        >
          <span className="icon icon-outlined">chevron_left</span>
          {translate('orderdetail.backtoorderlist')}
        </button>
        <a
          className="order-link-inverted inline-flex items-center gap-1 font-extrabold"
          target="_blank"
          rel="noreferrer"
          href={`${printPageBaseUrl}?id=${
            order?.id || order?.orderId
          }&currentTab=${
            isReseller
              ? tabsConstants.customerOrderDetail
              : tabsConstants.orderDetail
          }&print=true`}
        >
          <span className="icon icon-outlined">print</span>
          {translate('general.print')}
        </a>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="page-content h-fit">
          <SectionTitle>{translate('orderdetail.information')}</SectionTitle>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="lg:order-1">
              <Label
                title={translate('orderdetail.billingdetails')}
                direction="column"
              >
                <Label element="span">{order?.companyName}</Label>
                <Label element="span">{order?.companyAddress}</Label>
                <Label element="span">
                  <a href={`mailto:${order?.companyEmail}`} className="link">
                    {order?.companyEmail}
                  </a>
                </Label>
                <Label element="span">
                  <a href={`tel:${order?.companyPhone}`} className="link">
                    {order?.companyPhone}
                  </a>
                </Label>
              </Label>
            </div>

            <Label
              title={translate('orderdetail.deliverydetails')}
              direction="column"
              className="lg:order-3"
              element="div"
            >
              <Label element="span">
                {order?.customerInfo?.firstName} {order?.customerInfo?.lastName}
              </Label>
              <Label element="span">{order?.customerInfo?.address1}</Label>
              <Label element="span">
                {order?.customerInfo?.zip} {order?.customerInfo?.city}
              </Label>
              <Label element="span">{order?.customerInfo?.country}</Label>
            </Label>

            <div className="order-1 flex flex-col gap-4 lg:order-2">
              <div className="flex flex-col lg:items-end lg:text-right">
                <Label
                  title={translate('orderdetail.ordernumber')}
                  className="lg:justify-end"
                >
                  {order?.externalOrderID}
                </Label>
                <Label
                  title={translate('orderdetail.orderdate')}
                  className="lg:justify-end"
                >
                  {new Date(order.orderDate).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  })}
                </Label>
                <Label title={translate('orderdetail.orderstatus')}>
                  <OrderStatus
                    status={order.status}
                    color={order.statusColor}
                  />
                </Label>
              </div>
              <div className="flex flex-col lg:items-end lg:text-right">
                <Label
                  title={translate('orderdetail.paymentmethod')}
                  className="lg:justify-end"
                >
                  {order?.paymentMethod}
                </Label>
                <Label
                  title={translate('orderdetail.deliverymethod')}
                  className="lg:justify-end"
                >
                  {order?.deliveryMethod}
                </Label>
                <Label
                  title={translate('orderdetail.deliverydate')}
                  className="lg:justify-end"
                >
                  {order?.formattedActualDeliveryDate}
                </Label>
              </div>
              {!isReseller && order?.withdrawalStartUrl && order?.orderId && (
                <form
                  action={order.withdrawalStartUrl}
                  method="post"
                  className="mt-4 lg:text-right"
                >
                  <input
                    type="hidden"
                    name="orderSystemId"
                    value={order.orderId}
                  />
                  <button
                    type="submit"
                    className="button min-h-0 min-w-0 text-sm md:w-auto"
                  >
                    {translate('orderdetail.withdraw')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
        <div className="page-content flex h-fit flex-col overflow-clip">
          <SectionTitle>{translate('orderdetail.summary')}</SectionTitle>

          <div className="mb-8">
            {order?.orderRows?.map((row, index) => (
              <div
                key={index}
                className="border-border-primary @container mb-4 flex border-b pb-4 text-sm"
              >
                <div className="bg-background-tertiary aspect-square w-[88px] min-w-[88px]">
                  <img
                    src={row.image}
                    alt={row.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="@md:flex-row @md:justify-between @md:items-center ml-4 flex flex-1 flex-col gap-1">
                  <div className="flex flex-col gap-1">
                    <a
                      href={row.link.href}
                      className="link-inverted font-semibold"
                    >
                      {row.name}
                    </a>
                    {row?.articleNumber && (
                      <p className="text-accent text-xs">{row.articleNumber}</p>
                    )}
                    <p className="text-[#757575]">{row.quantityString}</p>
                  </div>
                  <p>{row.totalPrice}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-background-tertiary -mx-4 -mb-4 mt-auto p-4 md:-mx-6 md:-mb-6 md:p-6 lg:-mx-8 lg:-mb-8 lg:p-8">
            <SummaryRow
              label={translate('orderdetail.totalexcludingvat')}
              value={order?.orderTotal}
            />
            <SummaryRow
              label={translate('orderdetail.ordertotalvat')}
              value={order?.orderTotalVat}
            />
            <SummaryRow
              label={translate('orderdetail.grandtotal')}
              value={order?.orderGrandTotal}
              className="text-xl font-extrabold"
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default OrderHistoryB2CDetail;

const SectionTitle = ({ children, className }) => {
  return (
    <h3 className={cn('text-accent mb-8 text-xl font-extrabold', className)}>
      {children}
    </h3>
  );
};

const LabelTitle = ({ children, className, element }) => {
  const Wrapper = element || 'div';
  return (
    <Wrapper className={cn('text-[15px] font-bold leading-normal', className)}>
      {children}
    </Wrapper>
  );
};

const Label = ({
  children,
  className,
  title,
  direction = 'row',
  element = 'p',
}) => {
  const Wrapper = element;
  if (!children) return null;

  return (
    <Wrapper
      className={cn(
        'wrap-anywhere hyphens-auto text-[15px] leading-normal',
        title ? 'mb-2' : '',
        direction === 'column'
          ? 'flex flex-col gap-1'
          : 'flex flex-row flex-wrap gap-1',
        className
      )}
    >
      {title ? (
        <>
          <LabelTitle element="span">{title}: </LabelTitle>
          <span>{children}</span>
        </>
      ) : (
        <span>{children}</span>
      )}
    </Wrapper>
  );
};

const SummaryRow = ({ label, value, className }) => {
  return (
    <div className={cn('mb-1 flex justify-between', className)}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
};
